import React, { useEffect, useState, useRef, useCallback } from "react";
import { Box, Button, Typography, CircularProgress } from "@mui/material";
import { useMutation, gql } from "@apollo/client";
import { getPusherClient } from "../../../services/pusherClient";

// GraphQL mutation to send co-browsing events (server-side triggered via Pusher)
const SEND_CO_BROWSE_EVENT = gql`
  mutation SendCoBrowseEvent($sessionId: ID!, $type: String!, $data: JSON!) {
    sendCoBrowseEvent(sessionId: $sessionId, type: $type, data: $data)
  }
`;

// CoBrowsingView component
const CoBrowsingView = ({ sessionId, onEnd }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [hasReceivedEvents, setHasReceivedEvents] = useState(false);
  const [latestScreenshot, setLatestScreenshot] = useState(null);
  const [error, setError] = useState(null);
  const iframeRef = useRef(null);

  // Pusher state
  const pusherRef = useRef(null);
  const channelRef = useRef(null);
  const [pusherConnected, setPusherConnected] = useState(false);

  // Prevent re-initialization on re-renders
  const pusherInitializedRef = useRef(false);

  // Track iframe readiness and buffer events until ready
  const isIframeReadyRef = useRef(false);
  const eventBufferRef = useRef([]);

  // Track whether co-browsing is being ended intentionally
  const isEndingIntentionallyRef = useRef(false);

  // Chunk reassembly buffer for large events (keyed by chunkId)
  const chunkBufferRef = useRef({});

  // Navigation state
  const [isCustomerNavigating, setIsCustomerNavigating] = useState(false);
  const navigationTimeoutRef = useRef(null);
  const snapshotTimeoutRef = useRef(null);
  const NAVIGATION_GRACE_PERIOD = 15000;

  // Enhanced stats tracking
  const [stats, setStats] = useState({
    eventsReceived: 0,
    connectionUptime: 0,
    reconnectCount: 0,
    lastEventTime: null,
  });
  const statsRef = useRef({
    eventsReceived: 0,
    connectionStartTime: null,
    reconnectCount: 0,
  });

  // Update stats display every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (statsRef.current.connectionStartTime) {
        const uptime = Math.floor(
          (Date.now() - statsRef.current.connectionStartTime) / 1000
        );
        setStats((prev) => ({
          ...prev,
          eventsReceived: statsRef.current.eventsReceived,
          connectionUptime: uptime,
          reconnectCount: statsRef.current.reconnectCount,
        }));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Mutation for sending co-browsing events via backend
  const [sendCoBrowseEvent] = useMutation(SEND_CO_BROWSE_EVENT);

  // Function to forward events to the iframe
  const sendEventToIframe = useCallback(
    (eventType, data) => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        const message = {
          type: eventType,
          sessionId,
          data: data || {},
          timestamp: new Date().toISOString(),
        };

        if (!isIframeReadyRef.current) {
          console.log("⏳ Buffering event (iframe not ready):", eventType);
          eventBufferRef.current.push(message);
          return;
        }

        iframeRef.current.contentWindow.postMessage(message, "*");
      }
    },
    [sessionId]
  );

  // Handle incoming events (shared handler for all Pusher events)
  const handleCoBrowseEvent = useCallback(
    (event) => {
      // Track stats
      statsRef.current.eventsReceived++;
      setStats((prev) => ({
        ...prev,
        lastEventTime: new Date().toLocaleTimeString(),
      }));

      // Mark as connected for content events
      if (
        [
          "dom_event",
          "dom_event_batch",
          "screen_update",
          "co_browse_accepted",
        ].includes(event?.type)
      ) {
        setHasReceivedEvents(true);
        setIsConnected(true);
        setIsLoading(false);
      }

      // Only log non-frequent events
      if (
        event?.type !== "screen_update" &&
        event?.type !== "dom_event" &&
        event?.type !== "dom_event_batch" &&
        event?.type !== "mouse_move"
      ) {
        console.log("📨 Co-browse event received:", event?.type);
      }

      // Handle batched DOM events
      if (event?.type === "dom_event_batch" && event?.data?.events) {
        event.data.events.forEach((domEvent) => {
          sendEventToIframe("dom_event", { event: domEvent });
        });
        setIsConnected(true);
        setIsLoading(false);
        return;
      }

      // Handle chunked events (large payloads split across multiple messages)
      if (event?.type === "dom_event_chunk") {
        const { chunkId, chunkIndex, totalChunks, chunk, originalType } = event?.data || event || {};
        if (!chunkId || chunk === undefined) return;

        const buffer = chunkBufferRef.current;
        if (!buffer[chunkId]) {
          buffer[chunkId] = { chunks: {}, received: 0, totalChunks };
          // Clean up stale incomplete chunks after 30 seconds
          setTimeout(() => {
            delete buffer[chunkId];
          }, 30000);
        }

        if (!buffer[chunkId].chunks[chunkIndex]) {
          buffer[chunkId].chunks[chunkIndex] = chunk;
          buffer[chunkId].received++;
        }

        // All chunks received — reassemble
        if (buffer[chunkId].received === buffer[chunkId].totalChunks) {
          try {
            let fullJson = "";
            for (let i = 0; i < buffer[chunkId].totalChunks; i++) {
              fullJson += buffer[chunkId].chunks[i];
            }
            const reassembled = JSON.parse(fullJson);
            console.log("📦 Reassembled chunked event:", originalType || reassembled?.type, "(" + fullJson.length + " bytes)");
            delete buffer[chunkId];
            // Re-process as the original event type
            handleCoBrowseEvent({
              type: originalType || reassembled?.type,
              data: reassembled?.data,
              ...reassembled,
            });
          } catch (e) {
            console.error("Failed to reassemble chunked event:", e);
            delete buffer[chunkId];
          }
        }
        return;
      }

      // Forward event to iframe
      sendEventToIframe(event.type, event.data);

      // Handle specific event types
      switch (event?.type) {
        case "co_browse_accepted":
          setIsConnected(true);
          setIsLoading(false);
          break;

        case "co_browse_declined":
          setError("Customer declined co-browsing request");
          setIsLoading(false);
          setTimeout(() => {
            if (onEnd) onEnd();
          }, 3000);
          break;

        case "co_browse_ended":
        case "co_browse_end":
          if (isCustomerNavigating) {
            console.log("🔄 Ignoring end event - customer is navigating");
            break;
          }
          console.log("📴 Co-browsing ended");
          sendEventToIframe("co_browse_end", {});
          if (onEnd) onEnd();
          break;

        case "co_browse_error":
          setError(event.data?.error || "An error occurred with co-browsing");
          setIsLoading(false);
          console.error("Co-browse error:", event.data?.error);
          break;

        case "screen_update":
          if (event.data && event.data.screenshot) {
            setLatestScreenshot(event.data.screenshot);
            setIsConnected(true);
            setIsLoading(false);
          }
          break;

        case "dom_event":
          if (event.data && event.data.event) {
            if (snapshotTimeoutRef.current) {
              clearTimeout(snapshotTimeoutRef.current);
              snapshotTimeoutRef.current = null;
            }
            setIsConnected(true);
            setIsLoading(false);
          }
          break;

        case "page_change":
          console.log("📄 Customer navigated to:", event.data?.url);
          setIsConnected(true);
          setIsLoading(false);
          break;

        case "page_navigating":
          console.log("📱 Customer navigating to new page:", event.data);
          setIsCustomerNavigating(true);
          sendEventToIframe("page_navigating", event.data);

          if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
          }
          navigationTimeoutRef.current = setTimeout(() => {
            console.log("⏰ Navigation grace period expired");
            setIsCustomerNavigating(false);
            if (onEnd) onEnd();
          }, NAVIGATION_GRACE_PERIOD);
          break;

        case "session_resumed":
          console.log(
            "✅ Customer resumed co-browsing on new page:",
            event.data
          );
          setIsCustomerNavigating(false);
          setIsConnected(true);
          setIsLoading(false);

          if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
            navigationTimeoutRef.current = null;
          }

          sendEventToIframe("session_resumed", event.data);

          // Request snapshot if none arrives within 3 seconds
          if (snapshotTimeoutRef.current) {
            clearTimeout(snapshotTimeoutRef.current);
          }
          snapshotTimeoutRef.current = setTimeout(() => {
            console.log(
              "⚠️ No FullSnapshot received after resume, requesting fresh snapshot"
            );
            const channel = channelRef.current;
            if (channel) {
              channel.trigger("client-request-snapshot", {
                sessionId,
                data: { reason: "post_resume_timeout" },
                timestamp: new Date().toISOString(),
              });
            }
          }, 3000);
          break;

        case "visibility_change":
          console.log(
            "👁️ Customer tab visibility:",
            event.data?.isVisible ? "visible" : "hidden"
          );
          break;

        default:
          break;
      }
    },
    [onEnd, isCustomerNavigating, sendEventToIframe, sessionId]
  );

  // Initialize Pusher connection
  useEffect(() => {
    if (!sessionId) return;

    if (pusherInitializedRef.current) {
      console.log("Pusher already initialized, skipping");
      return;
    }

    let isCleaningUp = false;

    const initPusher = () => {
      if (isCleaningUp) return;

      try {
        console.log(
          "🔌 Initializing Pusher co-browse connection for agent..."
        );

        const pusherInstance = getPusherClient();

        pusherRef.current = pusherInstance;
        pusherInitializedRef.current = true;

        // Monitor connection state
        pusherInstance.connection.bind("connected", () => {
          if (isCleaningUp) return;
          console.log("✅ Pusher connected");
          setPusherConnected(true);
          statsRef.current.connectionStartTime = Date.now();
        });

        pusherInstance.connection.bind("disconnected", () => {
          console.log("📴 Pusher disconnected");
          setPusherConnected(false);
          if (!isCleaningUp && !isEndingIntentionallyRef.current) {
            statsRef.current.reconnectCount++;
          }
        });

        pusherInstance.connection.bind("error", (err) => {
          console.error("❌ Pusher connection error:", err);
        });

        // Subscribe to the co-browse channel
        const channelName = `private-cobrowse-${sessionId}`;
        console.log(`📡 Subscribing to channel: ${channelName}`);
        const channel = pusherInstance.subscribe(channelName);
        channelRef.current = channel;

        channel.bind("pusher:subscription_succeeded", () => {
          console.log("✅ Subscribed to co-browse channel");
          setPusherConnected(true);
        });

        channel.bind("pusher:subscription_error", (err) => {
          console.error("❌ Channel subscription error:", err);
          setError("Failed to subscribe to co-browse channel");
          setIsLoading(false);
        });

        // Bind to all client events from customer
        const clientEvents = [
          "client-dom-event",
          "client-dom-event-batch",
          "client-dom-event-chunk",
          "client-co-browse-accepted",
          "client-co-browse-declined",
          "client-co-browse-end",
          "client-co-browse-error",
          "client-page-change",
          "client-page-navigating",
          "client-session-resumed",
          "client-visibility-change",
          "client-mouse-move",
        ];

        clientEvents.forEach((eventName) => {
          channel.bind(eventName, (data) => {
            // Convert client event name to internal event type
            const type = eventName
              .replace("client-", "")
              .replace(/-/g, "_");
            handleCoBrowseEvent({ type, data: data?.data || data, ...data });
          });
        });

        // Bind to server events (triggered by backend via GraphQL relay)
        // These use hyphenated names without 'client-' prefix
        const serverEvents = [
          "co-browse-start",
          "co-browse-end",
          "co-browse-accepted",
          "co-browse-declined",
          "co-browse-error",
          "dom-event",
          "dom-event-batch",
          "dom-event-chunk",
          "page-change",
          "page-navigating",
          "session-resumed",
          "visibility-change",
        ];

        serverEvents.forEach((eventName) => {
          channel.bind(eventName, (data) => {
            const type = eventName.replace(/-/g, "_");
            handleCoBrowseEvent({ type, data: data?.data || data, ...data });
          });
        });
      } catch (err) {
        console.error("❌ Failed to initialize Pusher:", err);
        setError("Failed to connect to co-browsing service");
        setIsLoading(false);
      }
    };

    initPusher();

    return () => {
      isCleaningUp = true;

      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
      if (snapshotTimeoutRef.current) {
        clearTimeout(snapshotTimeoutRef.current);
        snapshotTimeoutRef.current = null;
      }

      if (isEndingIntentionallyRef.current) {
        pusherInitializedRef.current = false;
        const channel = channelRef.current;
        if (channel) {
          // Send end event via client event before unsubscribing
          try {
            channel.trigger("client-co-browse-end", {
              sessionId,
              data: { status: "ended" },
              timestamp: new Date().toISOString(),
            });
          } catch (e) {
            // Ignore if channel already unsubscribed
          }
          channel.unbind_all();
        }
        if (pusherRef.current) {
          // Only unsubscribe from channel - do NOT disconnect the shared singleton
          pusherRef.current.unsubscribe(
            `private-cobrowse-${sessionId}`
          );
        }
        channelRef.current = null;
        pusherRef.current = null;
        setPusherConnected(false);
      }
    };
  }, [sessionId]); // Only re-init if sessionId changes

  // Separate effect for updating event handler (avoids re-initializing Pusher)
  useEffect(() => {
    const channel = channelRef.current;
    if (!channel) return;

    // Re-bind client events with updated handler
    const clientEvents = [
      "client-dom-event",
      "client-dom-event-batch",
      "client-dom-event-chunk",
      "client-co-browse-accepted",
      "client-co-browse-declined",
      "client-co-browse-end",
      "client-co-browse-error",
      "client-page-change",
      "client-page-navigating",
      "client-session-resumed",
      "client-visibility-change",
      "client-mouse-move",
    ];

    const handlers = {};
    clientEvents.forEach((eventName) => {
      const handler = (data) => {
        const type = eventName.replace("client-", "").replace(/-/g, "_");
        handleCoBrowseEvent({ type, data: data?.data || data, ...data });
      };
      handlers[eventName] = handler;
      channel.unbind(eventName);
      channel.bind(eventName, handler);
    });

    // Re-bind server events (hyphenated names from backend relay)
    const serverEvents = [
      "co-browse-start",
      "co-browse-end",
      "co-browse-accepted",
      "co-browse-declined",
      "co-browse-error",
      "dom-event",
      "dom-event-batch",
      "dom-event-chunk",
      "page-change",
      "page-navigating",
      "session-resumed",
      "visibility-change",
    ];

    serverEvents.forEach((eventName) => {
      channel.unbind(eventName);
      channel.bind(eventName, (data) => {
        const type = eventName.replace(/-/g, "_");
        handleCoBrowseEvent({ type, data: data?.data || data, ...data });
      });
    });
  }, [handleCoBrowseEvent]);

  // Listen for messages from the iframe
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (!event.data || typeof event.data !== "object" || !event.data.type)
        return;

      switch (event.data.type) {
        case "iframe_ready":
          isIframeReadyRef.current = true;

          // Flush all buffered events
          eventBufferRef.current.forEach((msg) => {
            if (iframeRef.current && iframeRef.current.contentWindow) {
              iframeRef.current.contentWindow.postMessage(msg, "*");
            }
          });
          eventBufferRef.current = [];

          sendEventToIframe("co_browse_init", { sessionId });
          break;

        case "content_received":
          console.log("📺 Iframe confirmed content received - hiding overlay");
          setHasReceivedEvents(true);
          setIsConnected(true);
          setIsLoading(false);
          break;

        case "request_update": {
          const channel = channelRef.current;
          if (channel) {
            channel.trigger("client-request-snapshot", {
              sessionId,
              data: { timestamp: new Date().toISOString() },
            });
          }
          break;
        }

        case "request_snapshot": {
          console.log("📷 Iframe requested fresh snapshot:", event.data);
          const channel = channelRef.current;
          if (channel) {
            channel.trigger("client-request-snapshot", {
              sessionId,
              data: { reason: event.data?.reason || "iframe_request" },
              timestamp: new Date().toISOString(),
            });
          }
          break;
        }

        case "agent_interaction": {
          const interactionData = {
            interactionType: event.data.interactionType,
            ...event.data.data,
            timestamp: new Date().toISOString(),
          };

          const channel = channelRef.current;
          if (channel) {
            channel.trigger("client-agent-interaction", {
              sessionId,
              data: interactionData,
            });
          }
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener("message", handleIframeMessage);

    return () => {
      window.removeEventListener("message", handleIframeMessage);

      if (isEndingIntentionallyRef.current) {
        isIframeReadyRef.current = false;
        eventBufferRef.current = [];

        console.log(
          "✅ Cleanup: Intentional ending detected, sending co_browse_end"
        );
        sendCoBrowseEvent({
          variables: {
            sessionId,
            type: "co_browse_end",
            data: { status: "ended" },
          },
        }).catch((error) => {
          console.error("Error ending co-browsing:", error);
        });
      }
    };
  }, [sessionId, sendCoBrowseEvent, sendEventToIframe]);

  // Handle end of co-browsing
  const handleEndCoBrowsing = () => {
    isEndingIntentionallyRef.current = true;
    console.log(
      "🛑 User clicked End Co-browsing button - marking as intentional"
    );

    // Send end via Pusher client event
    const channel = channelRef.current;
    if (channel) {
      try {
        channel.trigger("client-co-browse-end", {
          sessionId,
          data: { status: "ended" },
          timestamp: new Date().toISOString(),
        });
      } catch (e) {
        // Fallback to GraphQL
        sendCoBrowseEvent({
          variables: {
            sessionId,
            type: "co_browse_end",
            data: { status: "ended" },
          },
        }).catch((error) => {
          console.error("Error ending co-browsing:", error);
        });
      }
    }

    if (onEnd) onEnd();
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        position: "relative",
        zIndex: 1100,
        marginLeft: "0 !important",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          padding: 2,
          backgroundColor: "primary.main",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">
          Customer Co-Browsing (Session:{" "}
          {sessionId ? `${sessionId.substring(0, 8)}...` : "N/A"})
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {pusherConnected
              ? "🚀 Connected"
              : isConnected
              ? "✅ Connected"
              : "⏳ Waiting"}
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={handleEndCoBrowsing}
          >
            End Co-Browsing
          </Button>
        </Box>
      </Box>

      {/* Debug Information - Stats Panel */}
      <Box
        sx={{
          padding: 1,
          backgroundColor: "rgba(0,0,0,0.1)",
          borderBottom: "1px solid rgba(0,0,0,0.2)",
          fontSize: "0.75rem",
          fontFamily: "monospace",
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="caption">
          🔍 Session: {sessionId ? sessionId.substring(0, 8) + "..." : "N/A"}
        </Typography>
        <Typography variant="caption">
          📡 Pusher {pusherConnected ? "✓" : "..."}
        </Typography>
        <Typography variant="caption">
          📊 Events: {stats.eventsReceived}
        </Typography>
        <Typography variant="caption">
          ⏱️ Uptime: {stats.connectionUptime}s
        </Typography>
        {stats.reconnectCount > 0 && (
          <Typography variant="caption" sx={{ color: "warning.main" }}>
            🔄 Reconnects: {stats.reconnectCount}
          </Typography>
        )}
        {stats.lastEventTime && (
          <Typography variant="caption">
            📨 Last: {stats.lastEventTime}
          </Typography>
        )}
        <Typography variant="caption">
          💬 {isConnected ? "Live" : "Waiting"}
        </Typography>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          backgroundColor: "#f5f5f5",
          overflow: "hidden",
        }}
      >
        {isLoading && !isConnected && !hasReceivedEvents && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 10,
            }}
          >
            <CircularProgress />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Waiting for customer to accept co-browsing...
            </Typography>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              zIndex: 10,
            }}
          >
            <Typography variant="h6" color="error">
              Error
            </Typography>
            <Typography variant="body1" color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              onClick={handleEndCoBrowsing}
              sx={{ mt: 2 }}
            >
              Close
            </Button>
          </Box>
        )}

        {/* Co-browsing iframe */}
        <iframe
          ref={iframeRef}
          src="/cobrowse-frame.html"
          title="Customer Screen"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
          }}
          sandbox="allow-scripts allow-same-origin"
        />
      </Box>
    </Box>
  );
};

export default CoBrowsingView;
